import pick from 'lodash/pick';
import uniqueId from 'lodash/uniqueId';

import {CONSUMABLE_THRESHOLD_STATUS} from './ConsumableConstants';

class ConsumableUsage {
  /**
   * @description Transforms the consumable summary or usage response object
   *     into a new ConsumableUsage instance. See constructor for params.
   */
  static apiResponseTransformer(dataTransferObject) {
    return new ConsumableUsage(dataTransferObject);
  }

  /**
   * @description Creates a new ConsumableUsage instance.
   *
   * @param {Object} options - initialization object
   * @param {number} options.consumedQuantity - quantity consumed (for this usage)
   * @param {string} [options.externalId] - authorization system identifier for
   *     this usage instance
   * @param {string} [options.fulfillmentId] - id of fulfillment of which this
   *     usage is a byproduct
   * @param {string} [options.fulfillmentExternalItemId] - the fulfillable item
         appeared in within the internal fulfillment needed request
   * @param {String} [options.id] - unique id for this consumable usage (will be
   *   auto-generated if none is provided)
   * @param {string} [options.offerId] - for delegation_type=GROUP: offer whose
   *     fulfillment resulted in this usage. Remove with temp_sls_2_5
   * @param {Date} [options.expirationDate] - ISO including time zone
   * @param {number} [options.overConsumedQuantity] - N/A for stock; from Sign
   * @param {boolean} [options.overQuota] - N/A for stock; from Sign
   * @param {number} options.remainingQuantity - quantity remaining (for this usage)
   * @param {Date} [options.startDate] - ISO including time zone
   * @param {string} [options.status] - status condition of the usage, such as EXPIRED
   * @param {string} [options.thresholdStatus] - used for badging in admin
   *     console. One of CONSUMABLE_THRESHOLD_STATUS. NONE means no threshold
   *     has been reached
   * @param {number} [options.thresholdValue] - when a threshold_status suggests
   *     a value, such as a percentage, value computed by consumable system
   * @param {number} options.totalQuantity - total quantity of consumable (for
   *     this usage)
   * @param {string} options.unit - the unit of measurement of the quantities,
   *     e.g., transactions, gigabytes, etc.
   */
  constructor(options) {
    Object.assign(
      this,
      pick(options, [
        'consumedQuantity',
        'externalId',
        'fulfillmentExternalItemId',
        'fulfillmentId',
        'id',
        'offerId',
        'overConsumedQuantity',
        'overQuota',
        'remainingQuantity',
        'status',
        'thresholdStatus',
        'thresholdValue',
        'totalQuantity',
        'unit',
      ])
    );

    ['expirationDate', 'startDate'].forEach((property) => {
      if (options[property]) {
        this[property] = new Date(options[property]);
      }
    });

    this.id = this.id ?? `consumable-usage-${uniqueId()}`;
  }

  /**
   * @description Method to determine whether this ConsumableUsage has had a
   *     threshold triggered or not
   *
   * @returns {boolean} whether the threshold has been reached/triggered
   */
  isThresholdTriggered() {
    return (
      this.thresholdStatus === CONSUMABLE_THRESHOLD_STATUS.DEPLETED ||
      this.thresholdStatus === CONSUMABLE_THRESHOLD_STATUS.PERCENTAGE_THRESHOLD
    );
  }
}

export default ConsumableUsage;
